import axios from 'axios';

// Use env var or default to same-origin '/api' in production
const API_URL = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - no need to add token as it's in the cookie
api.interceptors.request.use(
  (config) => {
    // Set withCredentials to true to include cookies in cross-origin requests
    config.withCredentials = true;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Avoid redirect loops on public/auth routes during OAuth handshake
      if (typeof window !== 'undefined') {
        const path = window.location && window.location.pathname ? window.location.pathname : '';
        const isPublicAuthRoute = path === '/login' || path.startsWith('/oauth2/');
        if (isPublicAuthRoute) {
          return Promise.reject(error);
        }
      }

      // Unauthorized, redirect to login
      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem('redirectAfterLogin');
          console.log('Unauthorized request detected, redirecting to login');
          window.location.replace('/login');
        } catch (e) {
          console.error('Error during redirect after 401:', e);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
};

// User API
export const userAPI = {
  validateAuth: () => api.get('/users/validate-auth'),
  logout: () => api.post('/users/logout'),
  getCurrentUser: () => api.get('/users/me'),
  updateCurrentUser: (userData) => api.put('/users/me', userData),
  getAllUsers: () => api.get('/users'),
  getUserById: (id) => api.get(`/users/${id}`),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/users/${id}`),
  changeUserRole: (id, role) => api.patch(`/users/${id}/role`, { role }),
};

// Whitelist API
export const whitelistAPI = {
  getAllWhitelistedUsers: () => api.get('/whitelist'),
  getWhitelistedUserById: (id) => api.get(`/whitelist/${id}`),
  createWhitelistedUser: (userData) => api.post('/whitelist', userData),
  updateWhitelistedUser: (id, userData) => api.put(`/whitelist/${id}`, userData),
  deleteWhitelistedUser: (id) => api.delete(`/whitelist/${id}`),
};

// Inventory API
export const inventoryAPI = {
  getAllItems: () => api.get('/inventory/list'),
  getItemById: (id) => api.get(`/inventory/${id}`),
  getAvailableItems: (startTime, endTime) => 
    api.get('/inventory/available', { params: { startTime, endTime } }),
  createItem: (itemData) => api.post('/inventory', itemData),
  createItemWithPhoto: (name, totalQuantity, file) => {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('totalQuantity', totalQuantity);
    if (file) {
      formData.append('file', file);
    }
    return axios.post(`${API_URL}/inventory/with-photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      withCredentials: true
    });
  },
  updateItem: (id, itemData) => api.put(`/inventory/${id}`, itemData),
  deleteItem: (id) => api.delete(`/inventory/${id}`),
  checkAvailability: (itemId, quantity, startTime, endTime) => 
    api.get('/inventory/check-availability', { 
      params: { itemId, quantity, startTime, endTime } 
    }),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/inventory/${id}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      withCredentials: true
    });
  },
  updateOrder: (itemIds) => api.patch('/inventory/order', { itemIds }),
};

// Booking API
export const bookingAPI = {
  getCurrentUserBookings: () => api.get('/bookings'),
  getBookingById: (id) => api.get(`/bookings/${id}`),
  createBooking: (bookingData) => api.post('/bookings', bookingData),
  updateBooking: (id, bookingData) => api.put(`/bookings/${id}`, bookingData),
  deleteBooking: (id) => api.delete(`/bookings/${id}`),
  archiveBooking: (id) => api.put(`/bookings/${id}/archive`),
  getAllBookings: () => api.get('/bookings/all'),
  getArchivedBookings: () => api.get('/bookings/archived'),
  getBookingsByStatus: (status) => api.get(`/bookings/status/${status}`),
  updateBookingStatus: (id, status) => api.put(`/bookings/${id}/status`, null, { params: { status } }),
  updatePickupStatus: (id, isPickedUp) => api.put(`/bookings/${id}/pickup`, null, { params: { isPickedUp } }),
  updateReturnStatus: (id, isReturned) => api.put(`/bookings/${id}/return`, null, { params: { isReturned } }),
  getOverlappingBookings: (startTime, endTime) => 
    api.get('/bookings/overlapping', { params: { startTime, endTime } }),
  getBookingsByInventoryItem: (itemId) => api.get(`/bookings/by-item/${itemId}`),
};

// Contest/Voting API
export const contestAPI = {
  // Voter-facing
  getActiveSubmissionsRandom: () => api.get('/contests/active/submissions/random'),
  getActiveProgress: () => api.get('/contests/active/progress'),
  getActiveUserVotes: () => api.get('/contests/active/user-votes'),
  // New singleton vote endpoint (no contestId needed)
  voteActive: (submissionId, score) => api.post(`/contests/active/submissions/${submissionId}/vote`, { score }),
  // Backward compatibility
  vote: (contestId, submissionId, score) => api.post(`/contests/${contestId}/submissions/${submissionId}/vote`, { score }),

  // Admin-facing (singleton)
  listContests: () => api.get('/contests'),
  createContest: (name, active = true) => api.post('/contests', { name, active }),
  activateContest: (contestId) => api.post(`/contests/${contestId}/activate`),
  addSubmissionActive: (text) => api.post(`/contests/active/submissions`, { text }),
  listSubmissionsActive: () => api.get(`/contests/active/submissions`),
  getActiveStats: () => api.get(`/contests/active/stats`),
  getActiveVoteDetails: ({ onlyCompletedVoters = true } = {}) =>
      api.get(`/contests/active/votes/details`, { params: { onlyCompletedVoters } }),
};

export default api;
